import { useEffect, useMemo, useRef } from 'react';

import { useAccountNumber } from '../../../../fetchHooks/useAccount';
import useFavoriteAccount from '../../../../fetchHooks/useFavoriteAccount';
import type { Account, AccountId } from '../../../../generated-types';
import { isValidAccountPaymentFrom } from '../../../../services/accountFilters';
import { CommonPaymentState } from '../../form/CommonPaymentState';

export default <TState extends CommonPaymentState>(
    state: TState,
    handleChange: (account: Account) => void,
    account?: Account,
    withFavoriteAccount = false,
): AccountId | undefined => {
    const fromAccountId = useRef<AccountId>();
    const { data: favoriteAccountNumber } = useFavoriteAccount({ enabled: withFavoriteAccount });
    const { data: favoriteAccount } = useAccountNumber(favoriteAccountNumber);

    const targetAccount: Account | undefined = useMemo(() => {
        if (account && !state.fromAccount) {
            if (isValidAccountPaymentFrom(account)) {
                return account;
            }
            if (favoriteAccount && isValidAccountPaymentFrom(favoriteAccount)) {
                return favoriteAccount;
            }
        }
        return undefined;
    }, [account, favoriteAccount, state.fromAccount]);

    useEffect(() => {
        if (targetAccount && !state.fromAccount) {
            handleChange(targetAccount);
        }
    }, [targetAccount, handleChange, state.fromAccount]);

    useEffect(() => {
        if (state.fromAccount && targetAccount) {
            fromAccountId.current = targetAccount.accountId;
        }
    }, [state.fromAccount, targetAccount]);

    return fromAccountId.current;
};
